/**
 * App.tsx - Main Swift Top-Up Location Search Application
 * 
 * This is the root application component that manages:
 * - View mode toggling (map/list) with URL state preservation
 * - Feature filtering (Swift ticket machines, Metro Stations, Payzone Agents)
 * - Search query and location selection
 * - Global event handling for accessibility and fallback mechanisms
 * 
 * Architecture:
 * - MapComponent and ListView are lazy-loaded to reduce initial bundle size
 * - In test environment, components are required directly to avoid async issues
 * - Global click handler captures filter toggle events as a fallback mechanism
 * - State synchronization between sidebar controls and main content area
 */

import React, { useState, Suspense } from "react";
import Header from "./components/Header";
import FilterPanel from "./components/FilterPanel";
import SearchPanel from "./components/SearchPanel";
import VisibleFeatures from "./components/VisibleFeatures";
import ViewToggle from "./components/ViewToggle";
import "./App.css";
import useWindowHeightWidth from "./utils/useWindowHeightWidth";

// =============================================================================
// LAZY-LOADED COMPONENT SETUP
// =============================================================================

/**
 * MapComponent lazy loader
 * - In test: uses require() to load synchronously (testing-library doesn't handle async)
 * - In production: uses React.lazy() for code splitting and improved initial load time
 */
let MapComponentLazy: any;
if (process.env.NODE_ENV === 'test') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const mod = require('./components/MapComponent');
  MapComponentLazy = (mod && (mod.default || mod)) as any;
} else {
  MapComponentLazy = React.lazy(() => import("./components/MapComponent"));
}

/**
 * ListView lazy loader
 * - In test: uses require() to load synchronously
 * - In production: uses React.lazy() to reduce main bundle parse/execution cost
 * This is especially important since ListView handles complex list rendering with ArcGIS data
 */
let ListViewLazy: any;
if (process.env.NODE_ENV === 'test') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const mod = require('./components/ListView');
  ListViewLazy = (mod && (mod.default || mod)) as any;
} else {
  ListViewLazy = React.lazy(() => import("./components/ListView"));
}

/**
 * App Component
 * 
 * State management breakdown:
 * 
 * SEARCH & FILTERS:
 * - searchQuery: Current user search text (postcode/area name)
 * - isSearching: Whether an active search is in progress
 * - selectedFilters: Array of active filter types (e.g., "BusStations", "MetroStations")
 * - selectedFeature: Currently selected feature object (populated from map or list click)
 * 
 * NAVIGATION & VIEW:
 * - viewMode: "map" or "list" (persisted in URL ?view=)
 * - visibleFeatures: Features visible in current map viewport
 * - isZoomedIn: True when map is zoomed beyond default level
 * 
 * MAP TARGETING:
 * - featureIdToZoom: Feature ID to focus on map (from list click or search selection)
 * - featureGeomToZoom: Full geometry object for map focus (includes x, y, sitetype)
 * - listCount: Number of features shown in list view (for screen readers)
 * 
 * STATE MANAGEMENT:
 * - apiError: Error message to display to user
 * - resetCounter: Incremented when clearing search; triggers child component resets
 */
function App() {
  // =============================================================================
  // CONFIGURATION & CONSTANTS
  // =============================================================================
  
  const FEATURE_LAYER_URL = process.env.REACT_APP_FEATURE_LAYER_URL;
  
  /**
   * Default filter types to show on initial load
   * Memoized to maintain referential equality across renders
   */
  const DEFAULT_FILTERS = React.useMemo(
    () => ["BusStations", "MetroStations", "PayzoneAgents"],
    [],
  );

  // =============================================================================
  // STATE DECLARATIONS
  // =============================================================================
  
  const [selectedFilters, setSelectedFilters] = useState<string[]>(DEFAULT_FILTERS);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [visibleFeatures, setVisibleFeatures] = useState<any[]>([]);
  const [isZoomedIn, setIsZoomedIn] = useState<boolean>(false);
  const [featureIdToZoom, setFeatureIdToZoom] = useState<number | undefined>();
  const [featureGeomToZoom, setFeatureGeomToZoom] = useState<any | undefined>(undefined);
  const [selectedFeature, setSelectedFeature] = useState<any | null>(null);
  const [listCount, setListCount] = useState<number>(0);
  const [apiError, setApiError] = useState<string | null>(null);
  const [resetCounter, setResetCounter] = useState<number>(0);

  React.useEffect(() => {
    // selectedFilters updated (no debug logs)
  }, [selectedFilters]);


  // =============================================================================
  // GLOBAL EVENT HANDLERS (FALLBACK MECHANISM)
  // =============================================================================

  /**
   * Global click handler for filter buttons
   * REASON: Fallback mechanism in case React event handlers don't propagate
   * CAPTURES: Click events on filter toggle buttons by aria-label
   * MAPS: aria-label → filter type → state update
   * 
   * This ensures filter toggles work even if React event delegation fails.
   * Captured at document level in the bubbling phase to catch all clicks.
   */
  React.useEffect(() => {
    const onDocClick = (ev: Event) => {
      try {
        const el = ev.target as HTMLElement | null;
        if (!el) return;
        const aria = el.getAttribute && el.getAttribute('aria-label');
        if (!aria) return;
        
        if (aria === 'Toggle Swift ticket machine filter') {
          setSelectedFilters((prev) => 
            prev.includes('BusStations') 
              ? prev.filter(f => f !== 'BusStations') 
              : [...prev, 'BusStations']
          );
        } else if (aria === 'Toggle Metro ticket machine filter') {
          setSelectedFilters((prev) => 
            prev.includes('MetroStations') 
              ? prev.filter(f => f !== 'MetroStations') 
              : [...prev, 'MetroStations']
          );
        } else if (aria === 'Toggle Payzone agents filter') {
          setSelectedFilters((prev) => 
            prev.includes('PayzoneAgents') 
              ? prev.filter(f => f !== 'PayzoneAgents') 
              : [...prev, 'PayzoneAgents']
          );
        }
      } catch (e) {
        // Silently ignore event parsing errors
      }
    };
    
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, []);

  // =============================================================================
  // VIEW MODE INITIALIZATION & PERSISTENCE
  // =============================================================================
  
  /**
   * Get initial view mode from URL or hash
   * PRECEDENCE:
   * 1. ?view=map or ?view=list query parameter
   * 2. #map or #list hash fragment
   * 3. Default to "list" if not specified
   * 
   * This allows shareable links to preserve the user's map/list preference
   */
  const getInitialViewMode = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get("view");
      if (v === "map" || v === "list") return v as "map" | "list";
      const hash = window.location.hash.replace("#", "");
      if (hash === "map" || hash === "list") return hash as "map" | "list";
    } catch (e) {
      // ignore URL parsing errors
    }
    return "list" as "map" | "list";
  };

  const [viewMode, setViewMode] = useState<"map" | "list">(getInitialViewMode);

  /**
   * Persist view mode to URL whenever it changes
   * Uses ?view=map or ?view=list query parameter for shareability
   * Ensures the view is preserved when page is refreshed
   */
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const currentView = params.get("view");
      if (currentView !== viewMode) {
        params.set("view", viewMode);
        window.history.replaceState(
          { view: viewMode },
          "",
          `${window.location.pathname}?${params.toString()}${window.location.hash}`
        );
      }
    } catch (e) {
      // ignore URL update errors
    }
  }, [viewMode]);

  // Lazy-loaded map component (renders inside Suspense)
  const MapComponentAny = MapComponentLazy as unknown as React.ComponentType<any>;

  // =============================================================================
  // SEARCH & INTERACTION HANDLERS
  // =============================================================================
  
  /**
   * Trigger a search with the given query
   * Initiates async search flow: query → geocoding → results → feature display
   */
  const handleSearch = (query: string) => {
    setIsSearching(true);
    setSearchQuery(query);
  };

  /**
   * Handle feature selection from sidebar or map
   * Accepts either a feature object (with geometry) or just an ID
   * 
   * LOGIC:
   * - If feature has geometry: open map view + highlight feature + show details
   * - If only ID available: switch to map and let MapComponent load geometry
   */
  const handleFeatureClick = (feature: any) => {
    if (!feature) return;
    
    if (feature.geometry && typeof feature.geometry.x !== 'undefined') {
      // Feature has geometry: switch to map and highlight with full feature object
      // preserves the sitetype for correct icon rendering
      openOnMap(feature);
      setSelectedFeature(feature);
    } else if (typeof feature.id !== 'undefined' && feature.id !== null) {
      // Only ID available: let map component query for full geometry
      setFeatureIdToZoom(feature.id);
    }
  };

  // Centralized clear/reset logic so we can invoke it from React handlers
  const doClearSearch = React.useCallback(() => {
    setSearchQuery("");
    setIsSearching(false);
    setSelectedFilters(DEFAULT_FILTERS);
    setSelectedFeature(null);
    setFeatureIdToZoom(undefined);
    setFeatureGeomToZoom(undefined);
    setVisibleFeatures([]);
    setApiError(null);
    // Keep current view mode (don't switch away from map if user is on map view)
    setResetCounter((c) => c + 1);
    // Ensure the search input is enabled and not readonly after clearing
    try {
      setTimeout(() => {
        try {
          const input = document.getElementById("autoCompleteSuggestions") as HTMLInputElement | null;
          if (input) {
            input.disabled = false;
            try { input.readOnly = false; } catch (e) {}
            try { input.tabIndex = 0; } catch (e) {}
            try { input.blur(); } catch (e) {}
          }
        } catch (e) {}
      }, 50);
    } catch (e) {}

    // Dev: inspect what's covering the search input after clearing
    try {
      if (process.env.NODE_ENV === 'development') {
        const inspect = () => {
          try {
            const input = document.querySelector('#autoCompleteSuggestions') as HTMLElement | null;
            if (!input) return;
            const r = input.getBoundingClientRect();
            const cx = Math.round(r.left + r.width / 2);
            const cy = Math.round(r.top + r.height / 2);
            const elems = document.elementsFromPoint(cx, cy);
            void elems;
          } catch (e) {}
        };
        // run immediately and after small delays to catch transient overlays
        setTimeout(inspect, 50);
        setTimeout(inspect, 300);
        setTimeout(inspect, 1000);
      }
    } catch (e) {}
  }, [DEFAULT_FILTERS]);

  // Attach a native event listener to the Clear Search button as a fallback
  React.useEffect(() => {
    try {
      const btn = document.querySelector('button[aria-label="Clear search and reset filters"]') as HTMLElement | null;
      if (!btn) return;
      const onNativeClick = (ev: Event) => {
        doClearSearch();
      };
      const onNativePointerUp = (ev: Event) => {
        // call as well in case click is suppressed
        doClearSearch();
      };
      btn.addEventListener('click', onNativeClick);
      btn.addEventListener('pointerup', onNativePointerUp);
      return () => {
        try { btn.removeEventListener('click', onNativeClick); btn.removeEventListener('pointerup', onNativePointerUp); } catch (e) {}
      };
    } catch (e) {
      // ignore
    }
  }, [doClearSearch]);

  // When a feature is deselected (selectedFeature becomes null), clear any pending featureIdToZoom
  React.useEffect(() => {
    if (selectedFeature === null) {
      setFeatureIdToZoom(undefined);
    }
  }, [selectedFeature]);

  // Open map view and focus a feature by id or geometry
  const openOnMap = (feature: any) => {
    // openOnMap invoked
    if (typeof feature === "number") {
      setSelectedFeature(null);
      setFeatureGeomToZoom(undefined);
      setFeatureIdToZoom(feature);
    } else if (feature && typeof feature === "object") {
      setSelectedFeature(feature);

      const sitetype =
        feature.sitetype || feature.siteType || feature.type || feature.Sitetype;

      if (feature.geometry && typeof feature.geometry.x !== "undefined") {
        setFeatureIdToZoom(undefined);
        setFeatureGeomToZoom({
          ...feature.geometry,
          sitetype,
          name: feature.name || feature.Name,
          address: feature.address || feature.Address,
        });
      } else if (
        typeof feature.x !== "undefined" &&
        typeof feature.y !== "undefined"
      ) {
        setFeatureIdToZoom(undefined);
        setFeatureGeomToZoom({
          ...feature,
          sitetype: feature.sitetype || sitetype,
        });
      } else if (
        typeof feature.longitude !== "undefined" &&
        typeof feature.latitude !== "undefined"
      ) {
        setFeatureIdToZoom(undefined);
        setFeatureGeomToZoom({
          x: feature.longitude,
          y: feature.latitude,
          spatialReference: { wkid: 4326 },
          sitetype,
          name: feature.name || feature.Name,
          address: feature.address || feature.Address,
        });
      } else if (typeof feature.id !== "undefined" && feature.id !== null) {
        setFeatureGeomToZoom(undefined);
        setFeatureIdToZoom(Number(feature.id));
      }
    }
    setViewMode("map");
  };

  const { windowWidth } = useWindowHeightWidth(); // Get window height and width
  // const mobileSize = `calc(100vh - ${bannerHeight}px - 200px)`;

  // Show correct height based on screen size
  // const mapHeight = windowWidth < 768 ? mobileSize : appHeight; // Minus 298px as t∂his is the height footer 'calc(100vh - 88px - 72px)'
  const leftPadding = windowWidth / 2 - 504;

  const ListViewAny = ListViewLazy as unknown as React.ComponentType<any>;

  return (
    <>
      <main className="App">
        {/* <a href="#main-content" className="skip-link">Skip to main content</a> */}
        <Header />

        <div className="wmnds-container wmnds-m-b-md wmnds-m-t-md">
          <div className="wmnds-grid wmnds-grid--align-start wmnds-grid--justify-between">
            <div className="wmnds-col-2-3">
              <h2>Find a Swift top-up location</h2>
            </div>
            <div className="wmnds-col-1-3" style={{ textAlign: "right" }}>
              <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
            </div>
          </div>
        </div>

        <div
          id="main-content"
            className={`app-container${viewMode === "list" ? " wmnds-container" : " wmnds-map-container"}`}
            style={{ paddingLeft: viewMode === 'map' && windowWidth >= 768 ? leftPadding : undefined }}
        >
          <div className="wmnds-grid wmnds-grid--justify-between">
            <aside className="sidebar wmnds-col-1 wmnds-col-md-1-3 wmnds-m-b-md">
              <div className="wmnds-grid">
                <div className="wmnds-col-1">
                    <button
                      className="wmnds-btn wmnds-btn--link wmnds-float-right"
                      aria-label="Clear search and reset filters"
                        onClick={() => { doClearSearch(); }}
                    >
                      Clear search
                    </button>
                </div>
              </div>
              <FilterPanel
                onFilterChange={setSelectedFilters}
                selectedFilters={selectedFilters}
                isMapView={viewMode === "map"}
              />
              <SearchPanel
                onSearch={handleSearch}
                isSearching={isSearching}
                resetCounter={resetCounter}
                onClear={doClearSearch}
              />
              {apiError &&
              !(
                viewMode === "list" &&
                apiError === "Failed to load visible map features"
              ) ? (
                <div className="wmnds-msg-summary wmnds-msg-summary--error wmnds-m-b-sm">
                  <div className="wmnds-msg-summary__header">
                    <svg className="wmnds-msg-summary__icon">
                      <use
                        xlinkHref="#wmnds-general-warning"
                        href="#wmnds-general-warning"
                      ></use>
                    </svg>
                    <h3 className="wmnds-msg-summary__title">{apiError}</h3>
                  </div>
                </div>
              ) : null}

              <span className="wmnds-screenreaders-only">
                <strong>
                  {viewMode === "map" ? visibleFeatures.length : listCount}
                </strong>{" "}
                {viewMode === "map" ? "locations shown" : "locations found"}
              </span>

              {/* List view is shown in the main content area; keep sidebar for controls only */}
              {viewMode === "map" && (
                <VisibleFeatures
                  features={visibleFeatures}
                  searchQuery={searchQuery}
                  onFeatureClick={handleFeatureClick}
                  onViewOnMap={openOnMap}
                  selectedFeature={selectedFeature}
                  isZoomedIn={isZoomedIn}
                />
              )}
            </aside>

            <div className="main-content wmnds-col-1 wmnds-col-md-2-3">
              {viewMode === "map" && (
                <>
                  <Suspense fallback={<div className="map-placeholder">Loading map…</div>}>
                    <MapComponentAny
                      featureLayerUrl={FEATURE_LAYER_URL}
                      selectedFilters={selectedFilters}
                      searchQuery={searchQuery}
                      onSearchStateChange={setIsSearching}
                      isSearching={isSearching}
                      onVisibleFeaturesChange={setVisibleFeatures}
                      onZoomLevelChange={setIsZoomedIn}
                      featureIdToZoom={featureIdToZoom}
                      featureGeomToZoom={featureGeomToZoom}
                      selectedFeature={selectedFeature}
                      onFeatureSelect={setSelectedFeature}
                      onApiError={setApiError}
                      apiError={apiError}
                      resetCounter={resetCounter}
                    />
                  </Suspense>

                  {/* VisibleFeatures moved to the sidebar; no duplicate here */}
                </>
              )}

              {viewMode === "list" && (
                <Suspense fallback={<div className="list-placeholder">Loading list…</div>}>
                  <ListViewAny
                    featureLayerUrl={FEATURE_LAYER_URL}
                    selectedFilters={selectedFilters}
                    searchQuery={searchQuery}
                    onSearchStateChange={setIsSearching}
                    onViewOnMap={openOnMap}
                    onCountChange={setListCount}
                    onApiError={setApiError}
                  />
                </Suspense>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default App;
